import { useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { GlobalQueryParams, QueryService } from './queryParams.interface';
import { useQueryParam } from './useQueryParams';
import { usePreviousQuery } from './usePreviousQuery';
import { usePreviousStore } from './usePreviousStore';

export enum NavigationStates {
  firstLoad = 'firstLoad',
  noQuery = 'noQuery',
  queryIsNotPrevQuery = 'queryIsNotPrevQuery',
  storeIsNotPrevStore = 'noPrev',
}

interface NavigationState<Params> {
  type: NavigationStates;
  path: string;
  params: Params;
}

export const useAltNavStates = <
  QueryParams extends Record<string, any> & GlobalQueryParams,
>(
  queryService: QueryService<QueryParams>,
): NavigationState<QueryParams> | null => {
  const location = useLocation();

  const queryParams = useQueryParam();
  const storeParams = queryService.useSelectMergedQuery();

  // Gets the previous value
  const prevQueryParams = usePreviousQuery<QueryParams>(queryParams);
  const prevStoreParams = usePreviousStore<QueryParams>(storeParams);

  // each route (page) has its own redux slice with a queryParam state and is merging global.queryParam.service and its redux slice with a global queryParam state.
  let mergedParams: QueryParams;
  // NavigationStates.firstLoad
  if (!prevQueryParams && !prevStoreParams) {
    // overwrite any defaults set in redux with queryParams sent in on first load.
    mergedParams = {
      ...storeParams,
      ...queryParams,
    };

    removeUnusedValues(mergedParams);

    return {
      type: NavigationStates.firstLoad,
      params: mergedParams,
      path: location.pathname,
    };
  }

  // NavigationStates.noQuery
  // load defaults from redux store param if there are any && !lodash.isEmpty(storeParams)
  if (isEmpty(queryParams)) {
    removeUnusedValues(storeParams);

    return {
      type: NavigationStates.noQuery,
      params: storeParams,
      path: location.pathname,
    };
  }

  if (!isDifferent(queryParams, storeParams)) {
    return null;
  }

  // these two are checking if url queryParams or storeParams changes one at the time.
  // QUESTION: why does query take precedence over store?
  // 1. NavigationStates.queryIsNotPrevQuery
  if (isDifferent(queryParams, prevQueryParams)) {
    // if query change then overwrite storeParams with query.
    mergedParams = {
      ...storeParams,
      ...queryParams,
    };

    removeUnusedValues(mergedParams);

    return {
      type: NavigationStates.queryIsNotPrevQuery,
      params: mergedParams,
      path: location.pathname,
    };
  }

  // 2. NavigationStates.storeIsNotPrevStore
  if (isDifferent(storeParams, prevStoreParams)) {
    // if store change then overwrite queryParams with store params.
    mergedParams = {
      ...queryParams,
      ...storeParams,
    };

    removeUnusedValues(mergedParams);

    return {
      type: NavigationStates.storeIsNotPrevStore,
      params: mergedParams,
      path: location.pathname,
    };
  }

  return null;
};

const isDifferent = <T>(queryParams: T, storeParams: T): boolean => {
  return !isEqual(queryParams, storeParams);
};

const removeUnusedValues = (mergedParams: GlobalQueryParams) => {
  Object.keys(mergedParams).forEach((key) => {
    if (mergedParams[key as keyof GlobalQueryParams] === null) {
      // eslint-disable-next-line no-param-reassign
      delete mergedParams[key as keyof GlobalQueryParams];
    }
  });
};
